import { CheckIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Switch as SwitchPrimitive } from 'radix-ui';
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react';
import { cn } from './cn';

const control =
  'w-full rounded-[var(--radius-field)] bg-sheet px-3.5 text-base text-ink ring-1 ring-line ring-inset placeholder:text-ink-3 transition-shadow duration-150 focus:outline-none focus:ring-2 focus:ring-action aria-[invalid=true]:ring-alert disabled:opacity-60';

export interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string;
  children: (id: string, describedBy: string | undefined) => ReactNode;
  className?: string;
  optional?: boolean;
}

/** Etichetta + controllo + suggerimento/errore, collegati per l'accessibilità. */
export function Field({ label, hint, error, children, className, optional }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const describedBy = error || hint ? hintId : undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-semibold text-ink">
        {label}
        {optional ? <span className="font-normal text-ink-3"> (facoltativo)</span> : null}
      </label>
      {children(id, describedBy)}
      {error ? (
        <p id={hintId} className="text-sm text-alert">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-ink-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function Input({ className, invalid, ...rest }, ref) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(control, 'h-12', className)}
      {...rest}
    />
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ className, invalid, rows = 4, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(control, 'min-h-24 resize-y py-3 leading-relaxed', className)}
      {...rest}
    />
  );
});

/** Select nativo: sull'iPhone apre la ruota di sistema, la scelta più comoda col pollice. */
export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }
>(function Select({ className, invalid, children, ...rest }, ref) {
  return (
    <div className="relative">
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(control, 'h-12 appearance-none pr-10', className)}
        {...rest}
      >
        {children}
      </select>
      <svg
        aria-hidden
        viewBox="0 0 16 16"
        className="pointer-events-none absolute top-1/2 right-3.5 h-4 w-4 -translate-y-1/2 text-ink-2"
      >
        <path
          d="M4 6l4 4 4-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
});

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}

export function Switch({ checked, onCheckedChange, label, description, disabled }: SwitchProps) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <label htmlFor={id} className="flex min-w-0 flex-col">
        <span className="text-[0.9375rem] font-medium text-ink">{label}</span>
        {description ? <span className="text-sm text-ink-3">{description}</span> : null}
      </label>
      <SwitchPrimitive.Root
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full bg-line transition-colors duration-200 data-[state=checked]:bg-ok disabled:opacity-50"
      >
        <SwitchPrimitive.Thumb className="block h-6 w-6 translate-x-0.5 rounded-full bg-white shadow-[0_1px_3px_rgb(0_0_0/0.25)] transition-transform duration-200 data-[state=checked]:translate-x-[1.375rem]" />
      </SwitchPrimitive.Root>
    </div>
  );
}

export interface CheckCircleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  color?: string;
  disabled?: boolean;
}

/** Cerchio da spuntare (attività, post): si riempie con un piccolo rimbalzo. */
export function CheckCircle({ checked, onChange, label, color, disabled }: CheckCircleProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: pulsante con ruolo checkbox, schema ARIA valido e più comodo da stilizzare.
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="group -m-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full disabled:opacity-50"
    >
      <span
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-inset transition-colors duration-150',
          checked ? 'text-white' : 'ring-ink-3 group-hover:ring-ink-2',
        )}
        style={checked ? { backgroundColor: color ?? 'var(--ok)', boxShadow: 'none' } : undefined}
      >
        {checked ? (
          <CheckIcon
            size={15}
            weight="bold"
            className="animate-[check-fill_240ms_ease-out]"
            aria-hidden
          />
        ) : null}
      </span>
    </button>
  );
}

export const SearchField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function SearchField({ className, ...rest }, ref) {
    return (
      <div className={cn('relative', className)}>
        <MagnifyingGlassIcon
          size={18}
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-ink-3"
        />
        <input ref={ref} type="search" className={cn(control, 'h-11 pl-10')} {...rest} />
      </div>
    );
  },
);
